import { useEffect, useState } from "react";
import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, verticalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

// Drag-and-drop list. `items` must have `id`. onReorder(ids) is called with the new order.
export const SortableList = ({ items, onReorder, grid = false, children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map((i) => i.id);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={grid ? rectSortingStrategy : verticalListSortingStrategy}>{children}</SortableContext>
    </DndContext>
  );
};

export const SortableItem = ({ id, className = "", children }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, opacity: isDragging ? 0.85 : 1 };
  const handle = (
    <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} data-testid={`drag-handle-${id}`} aria-label="Drag to reorder" className="touch-none cursor-grab active:cursor-grabbing p-1.5 rounded-md text-[#F8FAFC]/50 hover:text-[#F8FAFC] hover:bg-[#2C3E55]">
      <GripVertical className="w-4 h-4" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style} className={className}>
      {children(handle, isDragging)}
    </div>
  );
};

// Small "#n" box: type a position and press Enter (or blur) to move the item there.
export const PositionInput = ({ index, count, onMove, testId }) => {
  const [val, setVal] = useState(index + 1);
  useEffect(() => setVal(index + 1), [index]);
  const commit = () => {
    const n = Math.min(Math.max(parseInt(val, 10) || index + 1, 1), count);
    if (n - 1 !== index) onMove(n - 1);
    else setVal(index + 1);
  };
  return (
    <label className="inline-flex items-center gap-1 text-[11px] text-[#F8FAFC]/60" title="Type a position and press Enter">
      #
      <input data-testid={testId} type="number" min={1} max={count} value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-12 h-7 rounded-md bg-[#121E2B] border border-[#2C3E55] text-center text-xs text-[#F8FAFC] focus:border-[#E2705A] outline-none" />
    </label>
  );
};

export const moveTo = (ids, from, to) => arrayMove(ids, from, to);
